// 作用：v-class='foo' => class='${componentName}__foo'
// 增强：v-class={['foo', { bar: true }]} => class='${componentName}__foo ${componentName}__foo-bar'
// 特点：可以与class. staticClass同用
// 支持多级组件的变化
import _ from "lodash";
import Vue, { VNode, VNodeData, VNodeDirective } from "vue";

Vue.directive("class", {
  inserted: bindClass,
  update: bindClass,
});

function bindClass(el: HTMLElement, binding: VNodeDirective, vnode: VNode) {
  let addOnClass = "";
  const { value } = binding;
  const prefix = vnode.context!.$options.name!;
  if (_.isEmpty(value)) {
    addOnClass = prefix;
  } else if (_.isString(value)) {
    addOnClass = value
      .trim()
      .split(" ")
      .map((cls: string) => `${prefix}__${cls}`)
      .join(" ");
  } else if (_.isArray(value)) {
    const blockElement = `${prefix}__${value[0]}`;
    let modifier = value[1];
    if (modifier) {
      modifier = stringifyClass(modifier);
      modifier = modifier
        ? modifier
            .trim()
            .split(" ")
            .map((cls: string) => `${blockElement}-${cls}`)
        : "";
    }
    addOnClass = concat(blockElement, modifier);
  }

  if (!addOnClass) return;

  vnode.data = _.assign(vnode.data, {
    directiveClass: addOnClass,
  });

  const cls = genClassForVnode(vnode);

  if (cls !== (el as any).__prevClass) {
    el.setAttribute("class", cls);
    (el as any).__prevClass = cls;
  }
}

interface CustomVNodeData extends VNodeData {
  directiveClass: string;
}

// 1. copy from vue/src/platforms/web/util/class
// 2. add directiveClass
function genClassForVnode(vnode: VNode): string {
  let data = vnode.data!;
  let parentNode = vnode;
  let childNode = vnode;
  while (childNode.componentInstance) {
    childNode = (childNode.componentInstance as any)._vnode;
    if (childNode && childNode.data) {
      data = mergeClassData(
        childNode.data as CustomVNodeData,
        data as CustomVNodeData
      );
    }
  }
  while (parentNode.parent) {
    parentNode = parentNode.parent;
    if (parentNode && parentNode.data) {
      data = mergeClassData(
        data as CustomVNodeData,
        parentNode.data as CustomVNodeData
      );
    }
  }
  const staticClass = concat(
    (data as CustomVNodeData).directiveClass,
    data.staticClass
  );
  return renderClass(staticClass, data.class);
}

function mergeClassData(
  child: CustomVNodeData,
  parent: CustomVNodeData
): {
  directiveClass: string;
  staticClass: string;
  class: any;
} {
  return {
    directiveClass: concat(child.directiveClass, parent.directiveClass),
    staticClass: concat(child.staticClass, parent.staticClass),
    class: child.class ? [child.class, parent.class] : parent.class,
  };
}

export function renderClass(
  staticClass: string | undefined,
  dynamicClass: any
): string {
  if (staticClass || dynamicClass) {
    return concat(staticClass, stringifyClass(dynamicClass));
  }
  /* istanbul ignore next */
  return "";
}

export function concat(a?: string, b?: string): string {
  return a ? (b ? `${a} ${b}` : a) : b || "";
}

export function stringifyClass(value: any): string {
  if (Array.isArray(value)) {
    return stringifyArray(value);
  }
  if (_.isObject(value)) {
    return stringifyObject(value);
  }
  if (typeof value === "string") {
    return value;
  }
  return "";
}

function stringifyArray(value: any[]): string {
  let res = "";
  let stringified;
  for (let i = 0; i < value.length; i++) {
    stringified = stringifyClass(value[i]);
    if (stringified && stringified !== "") {
      if (res) res += " ";
      res += stringified;
    }
  }
  return res;
}

function stringifyObject(value: any): string {
  let res = "";
  for (const key in value) {
    if (value[key]) {
      if (res) res += " ";
      res += key;
    }
  }
  return res;
}
